'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from 'next-themes';
import Navbar from '@/components/web/Navbar';
import Footer from '@/components/web/Footer';
import { useTranslations } from 'next-intl';

// ===== DESKTOP VIDEO SETTINGS =====
// Hier anpassen für Desktop/Laptop-Ansicht
const VIDEO_1_DESKTOP = {
  x: 2,            // links(-) / rechts(+) in px
  y: -7.8,            // oben(-) / unten(+) in px
  scale: 0.9,        // Gesamtgröße (kleiner = Video kleiner)
  stretchX: 1,   // Breite quetschen/strecken
  stretchY: 1.14,   // Höhe quetschen/strecken
};

const VIDEO_2_DESKTOP = {
  x: 8,            // links(-) / rechts(+) in px
  y: -6,           // oben(-) / unten(+) in px
  scale: 1,        // Gesamtgröße
  stretchX: 0.98,  // Breite quetschen/strecken
  stretchY: 1.13,  // Höhe quetschen/strecken
};

// ===== MOBILE VIDEO SETTINGS =====
// Hier anpassen für Handy-Ansicht
const VIDEO_1_MOBILE = {
  x: 1.5,          // links(-) / rechts(+) in px
  y: 16,           // oben(-) / unten(+) in px
  scale: 0.79,     // Gesamtgröße (kleiner = Video kleiner)
  stretchX: 1,     // Breite quetschen/strecken
  stretchY: 1.15,  // Höhe quetschen/strecken
};

const VIDEO_2_MOBILE = {
  x: 4,            // links(-) / rechts(+) in px
  y: 16,           // oben(-) / unten(+) in px
  scale: 1,        // Gesamtgröße
  stretchX: 0.98,  // Breite quetschen/strecken
  stretchY: 1.13,  // Höhe quetschen/strecken
};

// Carousel Slider Component
function CarouselSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useTheme();

  const slides = [
    {
      id: 1,
      imageDark: '/carousel/dark/slide-1.png',
      imageLight: '/carousel/light/slide-1.png',
      title: 'Advanced Analytics',
      description: 'Track your performance with detailed charts',
    },
    {
      id: 2,
      imageDark: '/carousel/dark/slide-2.png',
      imageLight: '/carousel/light/slide-2.png',
      title: 'Trade Calendar',
      description: 'Plan and review your trading sessions',
    },
    {
      id: 3,
      imageDark: '/carousel/dark/slide-3.png',
      imageLight: '/carousel/light/slide-3.png',
      title: 'Risk Management',
      description: 'Monitor your risk across all positions',
    },
    {
      id: 4,
      imageDark: '/carousel/dark/slide-4.png',
      imageLight: '/carousel/light/slide-4.png',
      title: 'Performance Reports',
      description: 'Generate comprehensive trading reports',
    },
    {
      id: 5,
      imageDark: '/carousel/dark/slide-5.png',
      imageLight: '/carousel/light/slide-5.png',
      title: 'AI Insights',
      description: 'Get intelligent recommendations',
    },
  ];

  // Auto-advance every 4 seconds
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isHovered, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const getSlidePosition = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return 'center';
    if (diff === 1 || diff === -(slides.length - 1)) return 'right';
    if (diff === -1 || diff === slides.length - 1) return 'left';
    return 'hidden';
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Carousel Container */}
      <div className="relative h-[280px] md:h-[400px] lg:h-[500px] overflow-hidden">
        {/* Slides */}
        <div className="relative h-full flex items-center justify-center">
          {slides.map((slide, index) => {
            const position = getSlidePosition(index);
            
            return (
              <div
                key={slide.id}
                className={`
                  absolute transition-all duration-700 ease-in-out w-[85%] md:w-[70%]
                  ${position === 'center' ? 'z-20 scale-100 opacity-100' : ''}
                  ${position === 'left' ? 'z-10 scale-90 opacity-40 -translate-x-[60%] md:-translate-x-[70%]' : ''}
                  ${position === 'right' ? 'z-10 scale-90 opacity-40 translate-x-[60%] md:translate-x-[70%]' : ''}
                  ${position === 'hidden' ? 'z-0 scale-75 opacity-0' : ''}
                `}
                style={{
                  maxWidth: '900px',
                }}
              >
                <div className="relative rounded-lg overflow-hidden bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/20">
                  <Image
                    src={theme === 'dark' ? slide.imageDark : slide.imageLight}
                    alt={slide.title}
                    width={1200}
                    height={700}
                    className="w-full h-auto"
                    quality={100}
                  />
                  
                  {/* Text Overlay (nur auf center slide) */}
                  {position === 'center' && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-8">
                      <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">
                        {slide.title}
                      </h3>
                      <p className="text-sm md:text-base text-white/80">{slide.description}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows (erscheinen beim Hover auf Desktop, immer sichtbar auf Mobile) */}
        <div className={`${isHovered ? 'opacity-100' : 'opacity-0 md:opacity-0'} md:${isHovered ? 'opacity-100' : 'opacity-0'} transition-opacity`}>
          {/* Left Arrow */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:p-4 transition-all"
            aria-label="Previous slide"
          >
            <svg
              className="w-4 h-4 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Right Arrow */}
          <button
            onClick={goToNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:p-4 transition-all"
            aria-label="Next slide"
          >
            <svg
              className="w-4 h-4 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-2 mt-4 md:mt-8">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`
              transition-all duration-300
              ${
                index === currentIndex
                  ? 'w-8 h-2 bg-purple-500'
                  : 'w-2 h-2 bg-foreground/30 hover:bg-foreground/50'
              }
              rounded-full
            `}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function WebLandingPage() {
  const [mounted, setMounted] = useState(false);
  const { theme } = useTheme();
  const t = useTranslations('landing');

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }


  return (
    <div className="h-screen overflow-y-auto overflow-x-hidden bg-background text-foreground">
      <div className="w-full px-0 lg:px-0 bg-background">
        <Navbar />
        
        <main
  className="relative pt-24 md:pt-32 overflow-visible bg-background md:[padding-bottom:clamp(400px,40vw,1000px)]"
>
        <div className="relative z-10 max-w-3xl space-y-6 md:space-y-8 mx-auto px-4 md:px-6 xl:px-0">
            <div className="space-y-3 md:space-y-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight">
  <span className="block">{t('hero.title.line1')}</span>
  <span className="block">{t('hero.title.line2')}</span>
</h1>
<p className="text-sm md:text-lg text-foreground/60 max-w-2xl">
  {t('hero.subtitle')}
</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <Link href="/login" className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:bg-foreground/90 transition">
              {t('hero.cta.primary')}
              </Link>
              <Link href="/login" className="text-sm font-medium text-foreground/70 hover:text-foreground transition">
              {t('hero.cta.secondary')}
              </Link>
            </div>
          </div>

          <div className="mt-16" />

          <div
  className="pointer-events-none absolute left-1/2 -translate-x-[40%] w-full hidden md:block"
  style={{
    bottom: '0px',
    maxWidth: 'clamp(1400px, 90vw, 2400px)'
  }}
>

            <div className="relative mx-auto w-full scale-75 md:scale-90 lg:scale-100" style={{ transform: 'perspective(1400px) rotateX(18deg) rotateZ(-12deg) translateZ(0)', transformOrigin: 'bottom left', backfaceVisibility: 'hidden', willChange: 'transform' }}>
              <div className="relative overflow-hidden rounded-[18px]">
              <Image 
  src={theme === 'dark' ? '/hero-dashboard.png' : '/hero-dashboard1.png'} 
  alt="Trading dashboard preview" 
  width={5000} 
  height={3000} 
  quality={100} 
  priority 
  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 150vw, 2400px" 
  className="w-full h-auto object-cover" 
/>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-[22%]">
                  <div className="absolute inset-0 bg-gradient-to-l from-background to-transparent" />
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[22%]">
                  <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Hero Image (mit 3D Transform wie Desktop) */}
          <div
            className="md:hidden pointer-events-none relative w-full mt-8"
            style={{ maxWidth: '100vw', marginLeft: '-10%' }}
          >
            <div
              className="relative w-[120%]"
              style={{
                transform: 'perspective(1400px) rotateX(18deg) rotateZ(-12deg) translateZ(0) scale(1.1)',
                transformOrigin: 'bottom left',
                backfaceVisibility: 'hidden',
              }}
            >
              <div className="relative overflow-hidden rounded-[12px]">
                <Image
                  src={theme === 'dark' ? '/hero-dashboard.png' : '/hero-dashboard1.png'}
                  alt="Trading dashboard preview"
                  width={1200}
                  height={720}
                  quality={90}
                  className="w-full h-auto object-cover"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-[22%]">
                  <div className="absolute inset-0 bg-gradient-to-l from-background to-transparent" />
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[22%]">
                  <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </main>

        <section id="features" className="relative z-10 px-4 md:px-6 lg:px-16 mt-8 md:-mt-[100px] pb-8 bg-background">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
  {t('features.title.part1')}{' '}
  <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
    {t('features.title.part2')}
  </span>
</h2>
<p className="text-base md:text-lg text-foreground/60 max-w-2xl mx-auto">
  {t('features.subtitle')}
</p>
            </div>
          </div>
        </section>

        {/* Laptop Videos - YOUR EXISTING CODE */}
        <section className="relative z-10 px-4 md:px-6 lg:px-16 pt-8 pb-12 md:pb-20 bg-background">
          <div className="max-w-7xl mx-auto space-y-16 md:space-y-28">
            {/* ROW 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-8 lg:gap-10">
              <div className="lg:col-span-7 order-1">
                <div className="relative mx-auto w-full max-w-none lg:scale-[1.25] origin-left">
                  <div className="absolute z-0 pointer-events-none hidden md:block" style={{ left: '11%', top: '8%', width: '76%', height: '84%', borderRadius: '10px', background: `radial-gradient(closest-side, rgba(168,85,247,0.00) 58%, rgba(168,85,247,0.22) 70%, rgba(168,85,247,0.10) 82%, rgba(168,85,247,0.00) 100%)`, filter: 'blur(10px)', transform: 'scale(1.02)' }} />
                  {/* Desktop Video */}
                  <video autoPlay muted loop playsInline preload="metadata" className="absolute rounded-[6px] z-10 hidden md:block" style={{ left: '19%', top: '26%', width: '60%', height: '39.5%', objectFit: 'contain', transform: `translate(${VIDEO_1_DESKTOP.x}px, ${VIDEO_1_DESKTOP.y}px) scale(${VIDEO_1_DESKTOP.scale}) scaleX(${VIDEO_1_DESKTOP.stretchX}) scaleY(${VIDEO_1_DESKTOP.stretchY})`, transformOrigin: 'center center' }} src="/videos/feature-dashboard.mp4" />
                  {/* Mobile Video */}
                  <video autoPlay muted loop playsInline preload="metadata" className="absolute rounded-[4px] z-10 md:hidden" style={{ left: '19%', top: '11.5%', width: '60%', height: '54%', objectFit: 'contain', transform: `translate(${VIDEO_1_MOBILE.x}px, ${VIDEO_1_MOBILE.y}px) scale(${VIDEO_1_MOBILE.scale}) scaleX(${VIDEO_1_MOBILE.stretchX}) scaleY(${VIDEO_1_MOBILE.stretchY})`, transformOrigin: 'center center' }} src="/videos/feature-dashboard.mp4" />
                  <img src="/laptops/laptop-right.png" alt="Laptop showcase" className="relative z-20 w-full h-auto" draggable={false} />
                </div>
              </div>
              <div className="lg:col-span-5 space-y-3 md:space-y-4 order-2">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold tracking-tight">{t('laptopSection.row1.title')}</h3>
<p className="text-foreground/60 text-sm md:text-base lg:text-lg max-w-xl">{t('laptopSection.row1.description')}</p>
<ul className="mt-4 md:mt-6 space-y-2 md:space-y-3 text-sm md:text-base text-foreground/70">
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-purple-400 shrink-0" />{t('laptopSection.row1.feature1')}</li>
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-pink-400 shrink-0" />{t('laptopSection.row1.feature2')}</li>
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-400 shrink-0" />{t('laptopSection.row1.feature3')}</li>
</ul>
              </div>
            </div>

            {/* ROW 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-8 lg:gap-10">
              <div className="lg:col-span-5 space-y-3 md:space-y-4 order-2 lg:order-1">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold tracking-tight">{t('laptopSection.row2.title')}</h3>
<p className="text-foreground/60 text-sm md:text-base lg:text-lg max-w-xl">{t('laptopSection.row2.description')}</p>
<ul className="mt-4 md:mt-6 space-y-2 md:space-y-3 text-sm md:text-base text-foreground/70">
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-cyan-400 shrink-0" />{t('laptopSection.row2.feature1')}</li>
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-blue-400 shrink-0" />{t('laptopSection.row2.feature2')}</li>
<li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-emerald-400 shrink-0" />{t('laptopSection.row2.feature3')}</li>
</ul>
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative mx-auto w-full max-w-none lg:scale-[1.25] origin-right">
                  <div className="absolute z-0 pointer-events-none hidden md:block" style={{ left: '13%', top: '8%', width: '72%', height: '83%', borderRadius: '11px', background: `radial-gradient(closest-side, rgba(168,85,247,0.00) 58%, rgba(168,85,247,0.22) 70%, rgba(168,85,247,0.10) 82%, rgba(168,85,247,0.00) 100%)`, filter: 'blur(10px)', transform: 'scale(1.02)' }} />
                  {/* Desktop Video */}
                  <video autoPlay muted loop playsInline preload="metadata" className="absolute rounded-[6px] z-10 hidden md:block" style={{ left: '24%', top: '26%', width: '48.5%', height: '39.5%', objectFit: 'contain', transform: `translate(${VIDEO_2_DESKTOP.x}px, ${VIDEO_2_DESKTOP.y}px) scale(${VIDEO_2_DESKTOP.scale}) scaleX(${VIDEO_2_DESKTOP.stretchX}) scaleY(${VIDEO_2_DESKTOP.stretchY})`, transformOrigin: 'center center' }} src="/videos/feature-screenshots.mp4" />
                  {/* Mobile Video */}
                  <video autoPlay muted loop playsInline preload="metadata" className="absolute rounded-[4px] z-10 md:hidden" style={{ left: '24%', top: '11.5%', width: '48.5%', height: '54%', objectFit: 'contain', transform: `translate(${VIDEO_2_MOBILE.x}px, ${VIDEO_2_MOBILE.y}px) scale(${VIDEO_2_MOBILE.scale}) scaleX(${VIDEO_2_MOBILE.stretchX}) scaleY(${VIDEO_2_MOBILE.stretchY})`, transformOrigin: 'center center' }} src="/videos/feature-screenshots.mp4" />
                  <img src="/laptops/laptop-left.png" alt="Laptop showcase" className="relative z-20 w-full h-auto" draggable={false} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Automated Features - YOUR EXISTING CODE */}
        <section className="relative z-10 px-4 md:px-6 lg:px-16 py-12 md:py-20 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 md:mb-16 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
  {t('automated.title.part1')}{' '}
  <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">{t('automated.title.part2')}</span>
</h2>
<p className="text-base md:text-lg text-foreground/60 max-w-2xl mx-auto mb-4 md:mb-6">{t('automated.subtitle')}</p>
<Link href="#brokers" className="inline-flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition text-sm md:text-base">
  {t('automated.cta')}<span>→</span>
</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Card 1: Custom Preset */}
<div className="relative rounded-2xl md:rounded-3xl overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-900 p-5 md:p-8 min-h-[280px] md:min-h-[400px] flex flex-col">
<h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">{t('automated.cards.customPreset.title')}</h3>
<p className="text-white/80 text-sm md:text-base mb-auto">
  {t('automated.cards.customPreset.description')}
</p>

  {/* Bild – größer, nach LINKS versetzt (gespiegelt) */}
  <div className="mt-6 md:mt-10 relative">
    {/* Mobile version */}
    <div
      className="relative overflow-hidden md:hidden"
      style={{
        borderRadius: '10px',
        width: 'calc(100% + 60px)',
        maxWidth: '720px',
        marginRight: 'auto',
        marginLeft: '-20px',
      }}
    >
      <Image
        src="/videos/custom-preset.png"
        alt="Custom Preset Preview"
        width={720}
        height={400}
        className="w-full h-auto opacity-35"
        style={{ objectFit: 'contain' }}
        quality={100}
      />
    </div>
    {/* Desktop version */}
    <div
      className="relative overflow-hidden hidden md:block"
      style={{
        borderRadius: '10px',
        width: 'calc(100% + 140px)',
        maxWidth: '720px',
        marginRight: 'auto',
        marginLeft: '-50px',
      }}
    >
      <Image
        src="/videos/custom-preset.png"
        alt="Custom Preset Preview"
        width={720}
        height={400}
        className="w-full h-auto opacity-35"
        style={{ objectFit: 'contain' }}
        quality={100}
      />
    </div>
  </div>
</div>
              {/* Card 2: Unlimited Accounts */}
<div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-600 p-5 md:p-8 min-h-[280px] md:min-h-[400px] flex flex-col">
<h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
  {t('automated.cards.unlimitedAccounts.title')}
</h3>
<p className="text-white/80 text-sm md:text-base mb-auto">
  {t('automated.cards.unlimitedAccounts.description')}
</p>

  {/* Video – größer OHNE scale (bleibt scharf), nach rechts versetzt */}
  <div className="mt-6 md:mt-10 relative">
    {/* Mobile version */}
    <div
      className="relative overflow-hidden bg-white/10 md:hidden"
      style={{
        borderRadius: '10px',
        width: 'calc(100% + 60px)',
        maxWidth: '720px',
        marginLeft: 'auto',
        marginRight: '-20px',
      }}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="block w-full h-auto opacity-35"
        style={{ objectFit: 'contain' }}
        src="/videos/unlimited-accounts.mp4"
      />
    </div>
    {/* Desktop version */}
    <div
      className="relative overflow-hidden bg-white/10 hidden md:block"
      style={{
        borderRadius: '10px',
        width: 'calc(100% + 140px)',
        maxWidth: '720px',
        marginLeft: 'auto',
        marginRight: '-40px',
      }}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="block w-full h-auto opacity-35"
        style={{ objectFit: 'contain' }}
        src="/videos/unlimited-accounts.mp4"
      />
    </div>
  </div>
</div>
              {/* Card 3 */}
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-900 p-5 md:p-8 min-h-[320px] md:min-h-[500px] flex flex-col">
                <div className="relative z-10 min-h-[120px] md:min-h-[190px]">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">{t('automated.cards.automatedStats.title')}</h3>
                <p className="text-white/80 text-sm md:text-base mb-4 md:mb-8">{t('automated.cards.automatedStats.description')}</p>
                </div>
                {/* 3x3 Grid mit 9 separaten Bildern */}
<div className="absolute inset-0 z-0 p-4 md:p-8">
  {/* Media-Anchor: unten ausrichten wie bei Card 1/2 */}
  <div
    className="
      absolute left-4 right-4 bottom-4 md:left-8 md:right-8 md:bottom-8
      opacity-35
      [--stats-scale:1.15]
      md:[--stats-scale:1.2]
      lg:[--stats-scale:1.35]
      xl:[--stats-scale:1.5]
    "
    style={{
      transform: 'scale(var(--stats-scale)) translateX(-15px)',
      transformOrigin: 'bottom center',
    }}
  >
    {/* TOP ROW (nach rechts) */}
    <div
      className="grid grid-cols-3 gap-2"
      style={{ transform: 'translateX(14px)' }} // <<< HIER verschieben
    >
      {[
        { key: 'oben1', alt: 'Top Widget 1', rect: true },
        { key: 'oben2', alt: 'Top Widget 2', rect: true },
        { key: 'oben3', alt: 'Top Widget 3', rect: true },
      ].map((item) => (
        <div
          key={item.key}
          className={[
            'relative overflow-hidden bg-white/10',
            item.rect ? 'rounded-xl' : 'rounded-lg',
            item.rect ? 'aspect-[439/152]' : 'aspect-[1083/598]',
          ].join(' ')}
        >
          <Image
            src={`/features/stats/${item.key}.png`}
            alt={item.alt}
            fill
            className="object-contain"
            quality={100}
          />
        </div>
      ))}
    </div>

    {/* MIDDLE ROW (bleibt gleich) */}
    <div className="grid grid-cols-3 gap-2 mt-2">
      {[
        { key: 'mitte1', alt: 'Middle Widget 1', rect: false },
        { key: 'mitte2', alt: 'Middle Widget 2', rect: false },
        { key: 'mitte3', alt: 'Middle Widget 3', rect: false },
      ].map((item) => (
        <div
          key={item.key}
          className={[
            'relative overflow-hidden bg-white/10',
            item.rect ? 'rounded-xl' : 'rounded-lg',
            item.rect ? 'aspect-[439/152]' : 'aspect-[1083/598]',
          ].join(' ')}
        >
          <Image
            src={`/features/stats/${item.key}.png`}
            alt={item.alt}
            fill
            className="object-contain"
            quality={100}
          />
        </div>
      ))}
    </div>

    {/* BOTTOM ROW (nach rechts) */}
    <div
      className="grid grid-cols-3 gap-2 mt-2"
      style={{ transform: 'translateX(14px)' }} // <<< HIER verschieben
    >
      {[
        { key: 'unten1', alt: 'Bottom Widget 1', rect: true },
        { key: 'unten2', alt: 'Bottom Widget 2', rect: true },
        { key: 'unten3', alt: 'Bottom Widget 3', rect: true },
      ].map((item) => (
        <div
          key={item.key}
          className={[
            'relative overflow-hidden bg-white/10',
            item.rect ? 'rounded-xl' : 'rounded-lg',
            item.rect ? 'aspect-[439/152]' : 'aspect-[1083/598]',
          ].join(' ')}
        >
          <Image
            src={`/features/stats/${item.key}.png`}
            alt={item.alt}
            fill
            className="object-contain"
            quality={100}
          />
        </div>
      ))}
    </div>
  </div>
</div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== Carousel Slider Section ====== */}
        <section className="relative z-10 px-4 md:px-6 lg:px-16 py-12 md:py-20 pb-24 md:pb-20 bg-background">
  <div className="max-w-7xl mx-auto">
    {/* Header */}
    <div className="mb-8 md:mb-16 text-center">
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
  {t('carousel.title')}
</h2>
<p className="text-base md:text-lg text-foreground/60 max-w-2xl mx-auto">
  {t('carousel.subtitle')}
</p>
    </div>

    {/* Carousel */}
    <CarouselSlider />
  </div>
</section>

        <Footer />
      </div>
    </div>
  );
}
